import { Fragment, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { ChevronIcon, EmptyState, ErrorState, ExternalIcon, LoadingState } from "@signet/ui";
import { CreatorAvatar, useCreatorPhotos, useMe, type CreatorPhotos } from "@signet/web-kit";
import { addDays, dayLabel, dayLetter, duration, melbourneDay, melbourneTime, TARGET_MINUTES, TARGET_STREAMS, weekDays, weekLabel } from "../shared/week.ts";
import { BASE, cbKeys, count, fetchHistory, fetchWeek, roomUrl, type Status, type StreamDay, type Week, type WeekRow } from "./api.ts";
import { UsernamesPanel } from "./usernames.tsx";

/* ──────────────────────────────────────────────────────────────────────────
   CB Stats (Jay, 2026-11-05): one week, Monday to Sunday, every creator's
   Chaturbate streams against the target of 3 streams of 3 hours. Grouped as
   the weekly report was (didn't stream, didn't meet it, met it), slackers on
   top, with a dot per day so who is slacking reads at a glance. A row opens
   to that creator's streams, laid out like the daily update.
   ────────────────────────────────────────────────────────────────────────── */

const GROUPS: { status: Status; past: string; current: string }[] = [
  { status: "none", past: "Didn't stream", current: "Not streamed yet" },
  { status: "behind", past: "Didn't meet the target", current: "Behind" },
  { status: "on", past: "Met the target", current: "On target" },
];

function ago(iso: string): string {
  const minutes = Math.max(0, Math.round((Date.now() - Date.parse(iso)) / 60_000));
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  return hours < 48 ? `${hours}h ago` : `${Math.round(hours / 24)} days ago`;
}

export function CbStatsPage() {
  const { can } = useMe();
  const search = useSearch({ strict: false } as never) as { week?: string };
  const navigate = useNavigate();
  const week = useQuery({ queryKey: cbKeys.week(search.week ?? "this"), queryFn: () => fetchWeek(search.week), refetchInterval: 60_000 });
  const photos = useCreatorPhotos(BASE);
  const data = week.data;
  const setWeek = (monday: string | null) => navigate({ to: ".", search: (monday ? { week: monday } : {}) as never, replace: true });

  return (
    <div className="page cb-page">
      <header className="page-head">
        <div>
          <h1>CB Stats</h1>
          <p>
            Target for every creator: {TARGET_STREAMS} streams a week, {TARGET_MINUTES / 60} hours each. Chaturbate is checked every 3 minutes
            {data?.lastSweep ? `, last ${ago(data.lastSweep)}` : ""}.
          </p>
        </div>
      </header>

      {data ? (
        <div className="cb-week-bar">
          <button type="button" className="icon-button" aria-label="Week before" onClick={() => setWeek(addDays(data.week, -7))}>
            <ChevronIcon size={15} direction="left" />
          </button>
          <strong>{weekLabel(data.week)}</strong>
          <button type="button" className="icon-button" aria-label="Week after" disabled={data.current} onClick={() => setWeek(addDays(data.week, 7))}>
            <ChevronIcon size={15} direction="right" />
          </button>
          {data.current ? (
            <span className="cb-faint">This week, {data.daysLeft} {data.daysLeft === 1 ? "day" : "days"} left</span>
          ) : (
            <button type="button" className="text-button tiny accent" onClick={() => setWeek(null)}>
              This week
            </button>
          )}
        </div>
      ) : null}

      {week.isLoading ? (
        <LoadingState />
      ) : week.isError || !data ? (
        <ErrorState title="Couldn't load CB Stats" onRetry={() => week.refetch()} />
      ) : !data.rows.length ? (
        <section className="panel">
          <EmptyState title="No Chaturbate usernames yet" body={can("cbstats.account:create") ? "Add a creator's username below and their streams are tracked from then on." : "Someone with access adds creators' usernames, and their streams show here."} />
        </section>
      ) : (
        GROUPS.map((group) => {
          const rows = data.rows.filter((r) => r.status === group.status);
          if (!rows.length) return null;
          return <Group key={group.status} title={data.current ? group.current : group.past} status={group.status} rows={rows} week={data} photos={photos} />;
        })
      )}

      {can("cbstats.account:view") ? <UsernamesPanel photos={photos} /> : null}
    </div>
  );
}

function Group({ title, status, rows, week, photos }: { title: string; status: Status; rows: WeekRow[]; week: Week; photos: CreatorPhotos }) {
  const [open, setOpen] = useState<string | null>(null);
  const days = weekDays(week.week);
  return (
    <section className={`panel cb-group cb-${status}`}>
      <div className="panel-head">
        <h2>
          <span className="cb-group-dot" /> {title} <span className="cb-faint">{rows.length}</span>
        </h2>
      </div>
      <div className="panel-body flush">
        <div className="table cb-table">
          <div className="table-row head cb-row">
            <span>Creator</span>
            <span className="cb-days">
              {days.map((d) => (
                <span key={d} className={d === week.today ? "cb-today" : undefined}>{dayLetter(d).slice(0, 2)}</span>
              ))}
            </span>
            <span className="cb-num">3h streams</span>
            <span className="cb-num">Time live</span>
            <span className="cb-num">Most views</span>
            <span className="cb-num">Avg views</span>
            <span className="cb-num">Last week</span>
          </div>
          {rows.map((row) => (
            <Fragment key={row.account}>
              <button type="button" className={`table-row cb-row cb-click${open === row.account ? " open" : ""}`} aria-expanded={open === row.account} onClick={() => setOpen(open === row.account ? null : row.account)}>
                <span className="cb-who">
                  <CreatorAvatar name={row.creator} src={photos(row.creator)} size={30} />
                  <span className="cb-who-text">
                    <strong>
                      {row.creator}
                      {row.live ? <span className="cb-live" title="Live now">Live</span> : null}
                    </strong>
                    <small>{row.username}</small>
                  </span>
                </span>
                <span className="cb-days">
                  {row.days.map((d, i) => (
                    <DayDot key={days[i]} day={days[i]!} stream={d} week={week} trackedFrom={row.trackedFrom} />
                  ))}
                </span>
                <span className="cb-num">
                  <strong className={row.qualifying >= TARGET_STREAMS ? "cb-good" : row.streams ? "cb-warn" : "cb-bad"}>
                    {row.qualifying}/{TARGET_STREAMS}
                  </strong>
                  <Needs row={row} week={week} />
                </span>
                <span className="cb-num">
                  {duration(row.minutes)}
                  <small>{row.streams} {row.streams === 1 ? "stream" : "streams"}</small>
                </span>
                <span className="cb-num">{row.peak === null ? "" : count(row.peak)}</span>
                <span className="cb-num">{row.avg === null ? "" : count(row.avg)}</span>
                <span className="cb-num">
                  {row.lastWeek.tracked ? (
                    <>
                      {row.lastWeek.qualifying}/{TARGET_STREAMS}
                      <small>{duration(row.lastWeek.minutes)}</small>
                    </>
                  ) : (
                    <small>Not tracked</small>
                  )}
                </span>
              </button>
              {open === row.account ? <Detail row={row} /> : null}
            </Fragment>
          ))}
        </div>
      </div>
    </section>
  );
}

/** A day of the week: green for a 3 hour stream, amber for a shorter one, grey for none. */
function DayDot({ day, stream, week, trackedFrom }: { day: string; stream: StreamDay | null; week: Week; trackedFrom: string | null }) {
  const label = dayLabel(day);
  if (day > week.today) return <span className="cb-day future" title={`${label}: still to come`} />;
  if (trackedFrom && day < trackedFrom) return <span className="cb-day untracked" title={`${label}: not tracked yet`} />;
  if (!stream) return <span className="cb-day off" title={`${label}: didn't stream`} />;
  const met = stream.minutes >= TARGET_MINUTES;
  return (
    <span className={`cb-day ${met ? "met" : "short"}`} title={`${label}: ${duration(stream.minutes)}, most views ${count(stream.peak)}`}>
      {Math.floor(stream.minutes / 60)}:{String(stream.minutes % 60).padStart(2, "0")}
    </span>
  );
}

/** This week only: what is still needed to make the target, or that it can't be made any more. */
function Needs({ row, week }: { row: WeekRow; week: Week }) {
  if (!week.current || row.qualifying >= TARGET_STREAMS) return null;
  const needed = TARGET_STREAMS - row.qualifying;
  // Today still counts when there is no stream yet: a stream later today makes it.
  const todayDone = row.days[weekDays(week.week).indexOf(week.today)] !== null;
  const left = week.daysLeft - (todayDone ? 1 : 0);
  if (needed > left) return <small className="cb-bad">Can't make it</small>;
  return <small>Needs {needed} more</small>;
}

function Detail({ row }: { row: WeekRow }) {
  const history = useQuery({ queryKey: cbKeys.history(row.account), queryFn: () => fetchHistory(row.account) });
  const data = history.data;
  return (
    <div className="cb-detail">
      {history.isLoading ? (
        <LoadingState />
      ) : history.isError || !data ? (
        <ErrorState title="Couldn't load the streams" onRetry={() => history.refetch()} />
      ) : (
        <>
          <div className="cb-weeks">
            {data.weeks.map((w) =>
              w.tracked ? (
                <span key={w.week} className={`cb-week-chip ${w.qualifying >= TARGET_STREAMS ? "met" : w.streams ? "short" : "off"}`} title={`${weekLabel(w.week)}: ${w.streams} streams, ${duration(w.minutes)}`}>
                  <small>{weekLabel(w.week).split(" to ")[0]}</small>
                  <strong>{w.qualifying}/{TARGET_STREAMS}</strong>
                </span>
              ) : (
                <span key={w.week} className="cb-week-chip untracked" title={`${weekLabel(w.week)}: not tracked yet`}>
                  <small>{weekLabel(w.week).split(" to ")[0]}</small>
                  <strong>-</strong>
                </span>
              ),
            )}
            <a className="text-button tiny cb-room" href={roomUrl(row.username)} target="_blank" rel="noreferrer">
              chaturbate.com/{row.username} <ExternalIcon size={11} />
            </a>
          </div>
          {data.streams.length ? (
            <div className="table cb-streams">
              <div className="table-row head cb-stream-row">
                <span>Day</span>
                <span>Live</span>
                <span className="cb-num">Duration</span>
                <span className="cb-num">Most views</span>
                <span className="cb-num">Avg views</span>
                <span className="cb-num">New followers</span>
              </div>
              {data.streams.map((s) => (
                <div key={s.day} className="table-row cb-stream-row">
                  <span>{dayLabel(s.day)}</span>
                  <span className="cb-faint">{melbourneTime(s.start)} to {melbourneTime(s.end)}</span>
                  <span className={`cb-num ${s.minutes >= TARGET_MINUTES ? "cb-good" : "cb-warn"}`}>{duration(s.minutes)}</span>
                  <span className="cb-num">{count(s.peak)}</span>
                  <span className="cb-num">{count(s.avg)}</span>
                  <span className="cb-num">{s.followersGained ? `+${count(s.followersGained)}` : ""}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="cb-quiet">No streams since {dayLabel(melbourneDay(data.addedAt))}, when tracking started.</p>
          )}
        </>
      )}
    </div>
  );
}
